/**
 * مكان استخدام الإنترنت للجهاز:
 * يُحسب من المتصفح/الجهاز نفسه عبر خروجه للإنترنت (IP الاستخدام)،
 * وليس من سيرفر الـ API (الذي يعيد موقع البروفيدر/الاستضافة).
 */

const STORAGE_KEY = 'deviceInternetUsageLocation'
const CACHE_MAX_AGE_MS = 5 * 60 * 1000

export interface DeviceLocationInfo {
  display: string
  latitude?: number
  longitude?: number
  timeZone: string
  city?: string
  region?: string
  country?: string
  ipAddress?: string
  source: 'usage-ip' | 'cached' | 'timezone'
  updatedAt: string
}

function buildTimezoneFallback(): DeviceLocationInfo {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Unknown'
  return {
    display: timeZone,
    timeZone,
    source: 'timezone',
    updatedAt: new Date().toISOString(),
  }
}

function readCached(): DeviceLocationInfo | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as DeviceLocationInfo
    if (!parsed?.display) return null
    const age = Date.now() - new Date(parsed.updatedAt || 0).getTime()
    if (Number.isFinite(age) && age >= 0 && age <= CACHE_MAX_AGE_MS) {
      return { ...parsed, source: 'cached' }
    }
  } catch {
    // ignore
  }
  return null
}

function writeCached(info: DeviceLocationInfo): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(info))
  } catch {
    // ignore
  }
}

async function fetchJson(url: string, timeoutMs = 7000): Promise<Record<string, unknown> | null> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  try {
    const response = await fetch(url, { signal: controller.signal, cache: 'no-store' })
    if (!response.ok) return null
    return (await response.json()) as Record<string, unknown>
  } catch {
    return null
  } finally {
    clearTimeout(timer)
  }
}

function num(value: unknown): number | null {
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : null
}

function buildDisplay(parts: {
  lat: number
  lng: number
  city?: string
  region?: string
  country?: string
  timeZone?: string
}): string {
  const place = [parts.city, parts.region, parts.country].filter(Boolean).join(', ')
  const chunks = [
    `${parts.lat.toFixed(6)}, ${parts.lng.toFixed(6)}`,
    place || undefined,
    parts.timeZone || undefined,
  ].filter(Boolean)
  return chunks.join(' | ')
}

/**
 * يحدد مكان استخدام الإنترنت من خروج الجهاز نفسه إلى الإنترنت.
 * لا يعتمد على سيرفر ERP (لتجنب موقع البروفيدر).
 */
async function resolveUsageIpLocation(): Promise<DeviceLocationInfo | null> {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Unknown'

  // 1) ipwho.is من المتصفح → IP خروج الجهاز (مكان الاستخدام)
  const ipWho = await fetchJson('https://ipwho.is/')
  if (ipWho && ipWho.success !== false) {
    const lat = num(ipWho.latitude)
    const lng = num(ipWho.longitude)
    if (lat != null && lng != null && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
      const city = String(ipWho.city || '').trim() || undefined
      const region = String(ipWho.region || '').trim() || undefined
      const country = String(ipWho.country || '').trim() || undefined
      const tz =
        String((ipWho.timezone as { id?: string } | undefined)?.id || timeZone).trim() || timeZone
      const ipAddress = String(ipWho.ip || '').trim() || undefined
      // تجاهل connection.isp / org عمداً — نريد مكان الاستخدام لا اسم البروفيدر
      return {
        display: buildDisplay({ lat, lng, city, region, country, timeZone: tz }),
        latitude: Number(lat.toFixed(6)),
        longitude: Number(lng.toFixed(6)),
        timeZone: tz,
        city,
        region,
        country,
        ipAddress,
        source: 'usage-ip',
        updatedAt: new Date().toISOString(),
      }
    }
  }

  // 2) احتياط: ipapi.co من المتصفح أيضاً
  const ipApi = await fetchJson('https://ipapi.co/json/')
  if (ipApi && !ipApi.error) {
    const lat = num(ipApi.latitude)
    const lng = num(ipApi.longitude)
    if (lat != null && lng != null && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
      const city = String(ipApi.city || '').trim() || undefined
      const region = String(ipApi.region || ipApi.region_code || '').trim() || undefined
      const country = String(ipApi.country_name || ipApi.country || '').trim() || undefined
      const tz = String(ipApi.timezone || timeZone).trim() || timeZone
      const ipAddress = String(ipApi.ip || '').trim() || undefined
      return {
        display: buildDisplay({ lat, lng, city, region, country, timeZone: tz }),
        latitude: Number(lat.toFixed(6)),
        longitude: Number(lng.toFixed(6)),
        timeZone: tz,
        city,
        region,
        country,
        ipAddress,
        source: 'usage-ip',
        updatedAt: new Date().toISOString(),
      }
    }
  }

  return null
}

/** تحديث مكان استخدام الإنترنت للجهاز الحالي. */
export async function refreshDeviceLocation(): Promise<DeviceLocationInfo> {
  const fresh = await resolveUsageIpLocation()
  if (fresh) {
    writeCached(fresh)
    return fresh
  }

  const cached = readCached()
  if (cached?.latitude != null && cached?.longitude != null) {
    return cached
  }

  const fallback = buildTimezoneFallback()
  writeCached(fallback)
  return fallback
}

export async function getDeviceLocation(): Promise<DeviceLocationInfo> {
  const cached = readCached()
  if (cached?.latitude != null && cached?.longitude != null) {
    return cached
  }
  return refreshDeviceLocation()
}

export function getCachedDeviceLocationDisplay(): string | null {
  return readCached()?.display ?? null
}

export function parseLatLngFromDisplay(display?: string | null): { lat: number; lng: number } | null {
  if (!display) return null
  const match = display.match(/(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/)
  if (!match) return null
  const lat = Number(match[1])
  const lng = Number(match[2])
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null
  return { lat, lng }
}
